import React, { useState, useMemo } from 'react';
import { Question, Exam, Category, Difficulty } from '../../types';
import { updateQuestionInExam, deleteQuestionFromExam } from '../../services/dataService';

interface AdminQuestionBankProps {
    categories: Category[];
    exams: Exam[];
    onUpdateExam: (exam: Exam) => void;
}

const AdminQuestionBank: React.FC<AdminQuestionBankProps> = ({
    categories,
    exams,
    onUpdateExam
}) => {
    const [qBankSearch, setQBankSearch] = useState('');
    const [qBankFilterSubject, setQBankFilterSubject] = useState('');
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [editingExamId, setEditingExamId] = useState<string | null>(null);

    // Flatten questions for Question Bank view
    const allQuestions = useMemo(() => {
        return exams.flatMap(exam =>
            exam.questions.map(q => ({
                ...q,
                examId: exam.id,
                examTitle: exam.title,
                categoryName: categories.find(c => c.id === exam.category)?.name || exam.category
            }))
        );
    }, [exams, categories]);

    const filteredQuestions = useMemo(() => {
        return allQuestions.filter(q => {
            const matchesSearch = q.text.toLowerCase().includes(qBankSearch.toLowerCase()) ||
                q.id.toLowerCase().includes(qBankSearch.toLowerCase());
            const matchesSubject = qBankFilterSubject ? (q.subject === qBankFilterSubject || q.categoryName === qBankFilterSubject) : true;
            return matchesSearch && matchesSubject;
        });
    }, [allQuestions, qBankSearch, qBankFilterSubject]);

    const handleSaveQuestion = async (e: React.FormEvent) => {
        e.preventDefault();
        if (editingQuestion && editingExamId) {
            const updatedExam = await updateQuestionInExam(editingExamId, editingQuestion);
            if (updatedExam) {
                onUpdateExam(updatedExam);
                setEditingQuestion(null);
                setEditingExamId(null);
                alert("Question updated successfully.");
            } else {
                alert("Failed to update question.");
            }
        }
    };

    const handleDeleteQuestion = async (examId: string, questionId: string) => {
        if (window.confirm("Are you sure you want to delete this question? This cannot be undone.")) {
            const updatedExam = await deleteQuestionFromExam(examId, questionId);
            if (updatedExam) {
                onUpdateExam(updatedExam);
                alert("Question deleted successfully.");
            } else {
                alert("Failed to delete question.");
            }
        }
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col h-[calc(100vh-200px)]">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 rounded-lg">
                        <i className="fas fa-database"></i>
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 dark:text-white">Question Bank Manager</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{filteredQuestions.length} questions found</p>
                    </div>
                </div>

                <div className="flex gap-3 w-full md:w-auto">
                    <div className="relative flex-grow">
                        <i className="fas fa-search absolute left-3 top-2.5 text-slate-400 dark:text-slate-500 text-sm"></i>
                        <input
                            type="text"
                            placeholder="Search questions..."
                            className="pl-9 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm w-full md:w-64 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-550 focus:ring-2 focus:ring-orange-500 outline-none"
                            value={qBankSearch}
                            onChange={(e) => setQBankSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                        value={qBankFilterSubject}
                        onChange={(e) => setQBankFilterSubject(e.target.value)}
                    >
                        <option value="">All Subjects</option>
                        {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="flex-1 overflow-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10 shadow-sm">
                        <tr className="text-slate-600 dark:text-slate-400 text-sm">
                            <th className="px-6 py-3 font-semibold w-1/2">Question Stem</th>
                            <th className="px-6 py-3 font-semibold">Exam / Category</th>
                            <th className="px-6 py-3 font-semibold">Diff / Subject</th>
                            <th className="px-6 py-3 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {filteredQuestions.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="p-12 text-center text-slate-500">
                                    No questions found matching your filters.
                                </td>
                            </tr>
                        ) : (
                            filteredQuestions.slice(0, 50).map((q) => (
                                <tr key={q.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-slate-800 dark:text-slate-200 line-clamp-2">{q.text}</div>
                                        <div className="text-xs text-slate-550 mt-1 font-mono">{q.id}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">{q.categoryName}</div>
                                        <div className="text-xs text-slate-500 dark:text-slate-455 truncate max-w-[150px]" title={(q as any).examTitle}>{(q as any).examTitle}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1">
                                            <span className={`text-xs px-2 py-0.5 rounded w-fit ${q.difficulty === 'Hard' ? 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400' : q.difficulty === 'Medium' ? 'bg-yellow-100 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-450' : 'bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400'}`}>
                                                {q.difficulty}
                                            </span>
                                            <span className="text-xs text-slate-500 dark:text-slate-400">{q.subject}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => {
                                                setEditingQuestion(q);
                                                setEditingExamId((q as any).examId);
                                            }}
                                            className="p-2 text-slate-400 dark:text-slate-550 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/20 rounded transition-colors shadow-sm"
                                            title="Edit"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                            onClick={() => handleDeleteQuestion((q as any).examId, q.id)}
                                            className="p-2 text-slate-400 dark:text-slate-555 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors shadow-sm"
                                            title="Delete"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {filteredQuestions.length > 50 && (
                    <div className="p-4 text-center text-xs text-slate-400 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700">
                        Showing first 50 results. Use search to filter more.
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editingQuestion && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-955/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="font-bold text-slate-800 dark:text-white">Edit Question</h3>
                            <button onClick={() => setEditingQuestion(null)} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-450">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <form id="edit-q-form" onSubmit={handleSaveQuestion} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Question Text</label>
                                    <textarea
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                                        rows={3}
                                        value={editingQuestion.text}
                                        onChange={e => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
                                        required
                                    ></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Image URL (Optional)</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                                        value={editingQuestion.imageUrl || ''}
                                        onChange={e => setEditingQuestion({ ...editingQuestion, imageUrl: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {editingQuestion.options.map((opt, idx) => (
                                        <div key={opt.id}>
                                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Option {opt.id}</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-sm text-slate-900 dark:text-slate-100"
                                                value={opt.text}
                                                onChange={e => {
                                                    const newOptions = [...editingQuestion.options];
                                                    newOptions[idx] = { ...opt, text: e.target.value };
                                                    setEditingQuestion({ ...editingQuestion, options: newOptions });
                                                }}
                                                required
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Correct Answer</label>
                                        <select
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                                            value={editingQuestion.correctOptionId}
                                            onChange={e => setEditingQuestion({ ...editingQuestion, correctOptionId: e.target.value })}
                                        >
                                            <option value="A">A</option>
                                            <option value="B">B</option>
                                            <option value="C">C</option>
                                            <option value="D">D</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Difficulty</label>
                                        <select
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                                            value={editingQuestion.difficulty}
                                            onChange={e => setEditingQuestion({ ...editingQuestion, difficulty: e.target.value as Difficulty })}
                                        >
                                            <option value="Easy">Easy</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Hard">Hard</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subject</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                                            value={editingQuestion.subject || ''}
                                            onChange={e => setEditingQuestion({ ...editingQuestion, subject: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Explanation</label>
                                    <textarea
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-orange-500 outline-none text-slate-900 dark:text-slate-100"
                                        rows={3}
                                        value={editingQuestion.explanation}
                                        onChange={e => setEditingQuestion({ ...editingQuestion, explanation: e.target.value })}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                            <button
                                onClick={() => setEditingQuestion(null)}
                                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="edit-q-form"
                                className="px-4 py-2 bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-650 text-white rounded font-medium transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminQuestionBank;
